import { initializeApp, type FirebaseOptions } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

/** Must equal functions/region.js. A mismatch makes every callable 404. */
export const FUNCTIONS_REGION = 'europe-west1';

declare const __FIREBASE_WEBAPP_CONFIG__: string;

/**
 * Exported for its own test: a half-configured app fails later, somewhere
 * unrelated, so it fails here instead with a name worth reading.
 */
export function readWebappConfig(raw: string): FirebaseOptions {
  if (!raw) throw new Error('missing-firebase-config');
  try {
    return JSON.parse(raw) as FirebaseOptions;
  } catch {
    throw new Error('invalid-firebase-config');
  }
}

const useEmulators = import.meta.env.VITE_USE_EMULATORS === '1';

export const app = initializeApp(readWebappConfig(__FIREBASE_WEBAPP_CONFIG__));
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const fns = getFunctions(app, FUNCTIONS_REGION);

if (useEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9098', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8085);
  connectFunctionsEmulator(fns, '127.0.0.1', 5002);
  connectStorageEmulator(storage, '127.0.0.1', 9198);
}

/** One typed way to reach a callable, so no page hand-rolls httpsCallable. */
export function callable<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(fns, name);
  return async (data: TReq): Promise<TRes> => (await fn(data)).data;
}
